/* eslint-disable indent, no-case-declarations, react/jsx-filename-extension */
import * as React from 'react';
import { Elements } from 'prismic-reactjs';

import { Divider, Notification } from '@nelnet/unifi-components-react';

import { Info } from '@material-ui/icons';

import PrismicCode from './PrismicCode';
import PrismicTable from './PrismicTable';
import * as Type from '../Type';
import Link from '../../modules/Link';
import linkResolver from '../../../../utils/linkResolver';

// -- Function to add unique key to props
const propsWithUniqueKey = (props, key) => {
  return Object.assign(props || {}, { key });
};

const formatInlineCode = (children) => {
  const formattedChildren =
    Array.isArray(children) &&
    children.map((child) => {
      const re = /`(.+?)`/gi;
      const text = Array.isArray(child) && child[0];
      const result = text && text.match(re);

      return result
        ? text.split('`').reduce((acc, el, index) => {
            if (acc.length === 0) {
              return [el];
            } else {
              const codeIndex = (acc.length + 1) / 2 - 1;
              const codeValue = `${result[codeIndex]}`.replace(/`/g, '');
              el = el.replace(codeValue, '');
              const code =
                codeValue !== 'undefined' &&
                React.createElement(
                  'code',
                  propsWithUniqueKey({}, `${index}${codeIndex}${codeValue}`),
                  codeValue,
                );
              if (el) {
                return acc.concat(el);
              } else if (code) {
                return acc.concat(code);
              } else {
                return acc;
              }
            }
          }, [])
        : child;
    });

  return formattedChildren || children;
};

// @link https://prismic.io/docs/reactjs/beyond-the-api/html-serializer
const htmlSerializer = function (type, element, _content, children, key) {
  let props = { children: formatInlineCode(children) };

  switch (type) {
    case Elements.heading1:
      return <Type.H1 {...propsWithUniqueKey(props, key)} />;
    case Elements.heading2:
      const valueH2 = props?.children[0] && props.children[0][0];
      const anchorH2 = valueH2 ? valueH2.replace(/\s+/g, '-').toLowerCase() : '';
      return <Type.H2 {...propsWithUniqueKey({ ...props, id: anchorH2 }, key)} />;
    case Elements.heading3:
      const valueH3 = props?.children[0] && props.children[0][0];
      const anchorH3 = valueH3 ? valueH3.replace(/\s+/g, '-').toLowerCase() : '';
      return <Type.H3 {...propsWithUniqueKey({ ...props, id: anchorH3 }, key)} />;
    case Elements.heading4:
      return <Type.H4 {...propsWithUniqueKey(props, key)} />;
    case Elements.heading5:
      return <Type.H5 {...propsWithUniqueKey(props, key)} />;
    case Elements.heading6:
      return <Type.H6 {...propsWithUniqueKey(props, key)} />;
    case Elements.paragraph:
      return shortcodes(props, key);
    case Elements.list:
      return <Type.UL {...propsWithUniqueKey(props, key)} />;
    case Elements.oList:
      return <Type.OL {...propsWithUniqueKey(props, key)} />;
    case Elements.listItem:
      return <Type.LI {...propsWithUniqueKey(props, key)} />;
    case Elements.oListItem:
      return <Type.LI {...propsWithUniqueKey(props, key)} />;
    case Elements.em:
      return <Type.EM {...propsWithUniqueKey(props, key)} />;
    case Elements.strong:
      return <Type.Strong {...propsWithUniqueKey(props, key)} />;
    case Elements.preformatted:
      if (typeof props.children[0][0] === 'string' && props.children[0][0] === '[table]') {
        return <PrismicTable table={props.children[0]} />;
      } else {
        const code = props.children[0].filter((el) => typeof el === 'string').join('\n');
        return <PrismicCode {...propsWithUniqueKey({}, key)}>{code}</PrismicCode>;
      }
    case Elements.hyperlink:
      let href = element.data.url || linkResolver(element.data);
      if (href.includes('https://#')) {
        href = href.replace('https://', '');
      }
      props = Object.assign(
        {
          href,
          children: formatInlineCode(children),
        },
        key,
      );
      return <Link {...props} />;
    default:
      return null;
  }
};

export default htmlSerializer;

const shortcodes = (props, key) => {
  const content = props?.children[0] && props.children[0][0];
  const nStart = content && content.match(/\[notification(\s.*?)?\]/gim);

  if (content && nStart) {
    const getType = content.match(/type="(\w+)"/);
    const type = getType ? getType[1] : undefined;

    return (
      <Notification key={content} type={type} icon={<Info />} margin={{ bottom: 3 }}>
        {props?.children.reduce((acc, el) => {
          let elContent = Array.isArray(el) ? el[0] : el;

          if (typeof elContent === 'string') {
            elContent = elContent.replace(nStart[0], '');
            elContent = elContent.replace('[/notification]', '');
          }

          if (acc.length === 0) {
            return [elContent];
          } else {
            return acc.concat(elContent);
          }
        }, [])}
      </Notification>
    );
  }

  switch (content) {
    case '---':
      return <Divider {...propsWithUniqueKey({}, key)} />;
    default:
      return <Type.P {...propsWithUniqueKey(props, key)} />;
  }
};
